import {
    useRef,
    useState
} from 'react';

import {SimpleGrid} from '@mantine/core';

import {useActivityLogs} from '../hooks/useActivityLogs.ts';
import {useTableColumns} from '../hooks/useTableColumns.tsx';
import {useActivityLogsSelection} from '../hooks/useActivityLogsSelection.ts';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {update} from '../services/ActivityLogsService.ts';
import {buildUpdateActivityLogInput} from '../services/buildActivityLogInput.ts';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableTokenPagination} from "../../../components/DataTableTokenPagination.tsx";
import {ActivityLog} from '../types';
import {useQueryClient} from '@tanstack/react-query';

// FNP-328: optional recordId / recordType props let a detail page render its
// own activity feed (e.g. customer-subscriptions Profile Activity tab passes
// {recordId: subscriptionId, recordType: 'CustomerSubscription'}). Without
// them the table renders the full unfiltered list (existing /activity-logs
// index page).
export const RecordsTable = ({recordId, recordType}: {recordId?: string, recordType?: string} = {}) => {
    const {selectedRecords, setSelectedRecords} = useActivityLogsSelection();
    const {columns} = useTableColumns();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<ActivityLog | null>(null);
    const queryClient = useQueryClient();

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useActivityLogs(recordId, recordType);

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleDrawerSubmit = async (values: ActivityLog) => {
        if (selectedRecord?.id) {
            await update(selectedRecord.id, buildUpdateActivityLogInput(values) as ActivityLog);
            queryClient.invalidateQueries({queryKey: ['activity-logs']});
        }
    };

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) => {
                    setSelectedRecord(record);
                    setDrawerOpened(true);
                }}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken}
                                      handleLoadMore={fetchRecords}/>

            {drawerOpened && selectedRecord && (
                <FormDrawer
                    record={selectedRecord}
                    handleSubmit={handleDrawerSubmit}
                    opened={drawerOpened}
                    onClose={() => {
                        setDrawerOpened(false);
                        setSelectedRecord(null);
                    }}
                />
            )}
        </SimpleGrid>
    );
};
